import { ModalStatus } from "@/constants/modal";
import { LogLevel, LogService } from "@/services/logging";
import AppModule from "@/store/modules/app";
import ModalModule from "@/store/modules/modal";
import dayjs from "dayjs";
import { DAILY_AUTO_UPDATE_LIMIT } from "@/constants/app";
import { LocalStorageKeys } from "@/services/storage.service";
import DeviceModule from "@/store/modules/device";
import { checkTerminal } from "@/api/payment";
import { performanceInfo } from "../helpers/cross-platform";
import { ElMessageBox } from "element-plus";
import * as Sentry from "@sentry/vue";
import UserModule from "@/store/modules/user";

// Guards a single reload so overlapping update signals never reload twice.
let reloading = false;
function reloadOnce() {
  if (reloading) return;
  reloading = true;
  window.location.reload();
}

// Store the ServiceWorkerRegistration instance for later use
export function registerUpdate(e: any) {
  // the app received a new update
  if (e && e.detail) {
    AppModule.setServiceWorkerRegistration(e.detail);
  }
  AppModule.setIsUpdateAvailable(true);
  const now = dayjs();
  if (now.isAfter(AppModule.lastUpdateDate, "day")) {
    // it's been a day or more since the last auto-update
    // let's reset the daily auto-update count
    AppModule.setAutoUpdateCount(0);
  }
}

// Detect a worker that is already waiting.
// The `updated` event only fires once per install: a long-lived
// or slept device never re-surface a pending update.
// call this on mount and when the app regains focus.
export async function checkForUpdate(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  let reg: ServiceWorkerRegistration | undefined;
  try {
    reg = await navigator.serviceWorker.getRegistration();
    await reg?.update();
  } catch {
    // offline or transient, the periodic poll will retry
  }
  if (reg?.waiting) {
    AppModule.setServiceWorkerRegistration(reg);
    AppModule.setIsUpdateAvailable(true);
  }
}

// Apply a pending update right away on app boot, login screen,
export async function applyPendingUpdate(): Promise<void> {
  await checkForUpdate();
  if (!AppModule.isUpdateAvailable) return;
  LogService.info("Auto-update triggered at boot/login");
  autoUpdate();
}

// Update the app if there's an update available.
export async function updateApp(): Promise<void> {
  if (reloading) return;
  LogService.info("going to update the app");

  AppModule.setIsRefreshing(true);
  AppModule.setIsUpdateAvailable(false);

  let reg: ServiceWorkerRegistration | null | undefined = null;
  if ("serviceWorker" in navigator) {
    try {
      reg = await navigator.serviceWorker.getRegistration();
    } catch {
      // fall back to the registration captured by registerUpdate
    }
  }
  reg = reg ?? AppModule.serviceWorkerRegistration;
  const waiting = reg?.waiting;

  if (!waiting) {
    // Nothing waiting (already applied, or the reference was lost)
    reloadOnce();
    return;
  }

  // Reload as soon as the new worker activates.
  navigator.serviceWorker.addEventListener("controllerchange", reloadOnce, {
    once: true,
  });
  waiting.addEventListener("statechange", () => {
    if (waiting.state === "activated") reloadOnce();
  });
  setTimeout(reloadOnce, 3000);

  waiting.postMessage({ type: "SKIP_WAITING" });
}

/**
 * this funciton updates the app automatically without confirmation from the user
 */
export function autoUpdate() {
  if (!canRefresh()) return;
  const now = dayjs();
  if (
    now.isSame(dayjs(AppModule.lastUpdateDate), "day") &&
    AppModule.autoUpdateCount >= DAILY_AUTO_UPDATE_LIMIT
  ) {
    // we reached the auto update limit per day
    return;
  } else if (now.isSame(AppModule.lastUpdateDate, "day")) {
    AppModule.setAutoUpdateCount(AppModule.autoUpdateCount + 1);
  } else {
    // it's a different day, let's reset the update count and date and move forward with the update
    AppModule.setAutoUpdateCount(1);
  }
  AppModule.setLastUpdateDate(now.toISOString());
  updateApp();
}

/**
 * this function determines whether we can refresh the page or not
 */
export function canRefresh(): boolean {
  // let's prevent refreshing the page in the following cases:
  // 1- the user is filling custom fields (custom fields modal is open)
  if (ModalModule.status === ModalStatus.CustomFields) return false;
  // 2- a card payment is in progress
  if (ModalModule.status === ModalStatus.PaymentProgress) return false;
  // 3- preventing refresh is forced with a local storage item
  if (localStorage.getItem(LocalStorageKeys.PreventRefresh) === "1") {
    return false;
  }
  return true;
}

/**
 * returns the total memory used by the app in mb
 */
export function getTotalMemoryUsed(): number {
  if (window.performance && (window.performance as any).memory) {
    const memory = (window.performance as any).memory;
    const totalMemoryUsed = memory.usedJSHeapSize / (1024 * 1024);
    return totalMemoryUsed;
  }
  return -1;
}

export async function monitorAppPerformance() {
  // TODO Hamza: Currently we're logging only memory consumption
  // Soon we will need to add any useful info like tickets/bookings, synching issues ... etc
  try {
    if (window.performance && (window.performance as any).memory) {
      const memory = (window.performance as any).memory;
      const mem = {
        date: dayjs().utc().toISOString(),
        totalJSHeapSize: memory.totalJSHeapSize / (1024 * 1024),
        usedJSHeapSize: memory.usedJSHeapSize / (1024 * 1024),
        jsHeapSizeLimit: memory.jsHeapSizeLimit / (1024 * 1024),
        domMemory:
          (memory.totalJSHeapSize - memory.usedJSHeapSize) / (1024 * 1024),
      };
      const electronAppPerformance = await performanceInfo();

      LogService.info(`Current performance data of the app`, {
        params: {
          webPageMemoryConsumption: mem,
          electronAppPerformance,
        },
      });

      // Log memory consumption to Sentry
      Sentry.metrics.gauge("memory_usage", mem.totalJSHeapSize, {
        unit: "megabyte",
        tags: {
          userId: UserModule.userId || "",
          deviceId: DeviceModule.id || "",
          hardwareUuid:
            DeviceModule.hardwareInfo && DeviceModule.hardwareInfo.id
              ? DeviceModule.hardwareInfo.id
              : "",
        },
      });
    }
  } catch (err) {
    // Couldn't log memory consumption
    LogService.info(
      `Error when trying to log memory consumption`,
      {
        params: { error: err },
      },
      LogLevel.Error
    );
  }
}

export function checkManualHardwareUuid() {
  //// Hard coded hardware uuid needed when two devices have the same uuid within the same group
  // By accident we discovered that a hardware that is used in 19546 has the same uuid as another one used in 19545
  // duplicate hardware uuid is:
  // we had to hardcode a uuid in the localStorage to be able to assign the hardware to a device of 19546 in our backend
  // we used the hardcoded uuid: as a hardware uuid in localStorage
  // now whenever we get hardware uuid from Electron app, we need to check if it is 19546 and
  // then we hard code it based on a localStorage item manualHardCodedHardwareUuid we set manually already using TeamViewer in their devices
  const manualHardwareUuid = localStorage.getItem(
    LocalStorageKeys.manualHardwareUuid
  );
  if (manualHardwareUuid) {
    LogService.info(
      `Device has manual hardware uuid in its localStorage. Erasing hardwareUuid gotton from Electron app by the one we have in localStorage ${manualHardwareUuid}`
    );
    const hardwareInfo = DeviceModule.hardwareInfo;
    hardwareInfo.id = manualHardwareUuid;
    DeviceModule.setHardwareInfo(hardwareInfo);
  }
}

export enum TerminalConnectivity {
  Loading = 1,
  Connected = 2,
  Disconnected = 3,
}

/**
 * Get all connected terminals related to one group
 */
export async function getConnectedTerminals(): Promise<string[]> {
  try {
    LogService.info(`Going to get all connected terminals`);
    const resp = await checkTerminal();
    return resp.data.connectedTerminals;
  } catch (err) {
    LogService.info(
      `Error when checking terminal connectivity`,
      {
        params: { error: err },
      },
      LogLevel.Error
    );
    return [];
  }
}

export async function checkTerminalConnectivity(
  terminalId?: string
): Promise<TerminalConnectivity> {
  try {
    let resp: any;
    if (terminalId) {
      LogService.info(
        `Going to check connectivity for terminalId: ${terminalId}`
      );
      resp = await checkTerminal(terminalId);
    } else {
      LogService.info(`Going to get all connected terminals`);
      resp = await checkTerminal();
    }

    if (resp.data.connectedTerminals.includes(terminalId)) {
      LogService.info(`terminalId: ${terminalId} is connected`);
      return TerminalConnectivity.Connected;
    } else {
      LogService.info(`terminalId: ${terminalId} is disconnected`);
      return TerminalConnectivity.Disconnected;
    }
  } catch (err) {
    LogService.info(
      `Error when checking terminal connectivity, terminalId: ${terminalId}`,
      {
        params: { error: err },
      },
      LogLevel.Error
    );
    return TerminalConnectivity.Disconnected;
  }
}

export function openInternetNeededMessageBox() {
  LogService.info("Stopping user from proceeding action that needs internet");
  ElMessageBox.alert("You need internet to proceed this action", "Warning");
}

/**
 * Records a duration metric in Sentry
 * @param {string} name - The name of the metric to record.
 * @param {number} duration - The duration to record in milliseconds.
 */
export function recordOperationDuration(name: string, duration: number) {
  // Sentry.metrics.distribution(`${name}.duration`, duration, {
  //   unit: "millisecond",
  //   tags: {
  //     bookingUuid: String(BookingModule.selectedBookingUUID),
  //     userId: UserModule.userId || "",
  //     deviceId: DeviceModule.id || "",
  //     hardwareUuid:
  //       DeviceModule.hardwareInfo && DeviceModule.hardwareInfo.id
  //         ? DeviceModule.hardwareInfo.id
  //         : "",
  //   },
  // });

  // Log only if it exceeded 1s
  if (duration > 1000) {
    LogService.info(`${name} operation took ${(duration / 1000).toFixed(2)}s`, {
      params: { operation: name, duration },
    });
    console.log(`Operation ${name} took ${duration}ms`);
  }
}
